/**
 * OpenAI Service Integration
 * Handles all AI-powered features with role-based context
 * Supports: Chatbot, Scenario Generation, Feedback, Nudge Generation
 */

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface ChatbotContext {
  userType: 'platform_admin' | 'org_admin' | 'employee' | 'individual';
  userId: string;
  orgId?: string;
  userName?: string;
  recentActivity?: string[];
}

export interface ScenarioGenerationRequest {
  threatType: string;
  difficulty: 'easy' | 'medium' | 'hard';
  channel: 'email' | 'sms' | 'phone';
  targetAudience?: 'employee' | 'individual';
  scenarioNumber?: number;
}

export interface ScenarioTemplate {
  subject?: string;
  sender: string;
  content: string;
  redFlags: Array<{ type: string; description: string }>;
  risks: string[];
  isAiGenerated: boolean;
  isSafe?: boolean;
}

export interface FeedbackRequest {
  action: 'clicked' | 'reported' | 'deleted' | 'ignored';
  scenarioContent: string;
  isCorrect: boolean;
  userType: 'employee' | 'individual';
}

export interface FeedbackResponse {
  explanation: string;
  tips: string[];
  encouragement: string;
  nextSteps?: string;
}

class OpenAIService {
  private apiKey: string | null;
  private baseUrl: string;
  private isEnabled: boolean;

  constructor() {
    this.apiKey = import.meta.env.VITE_OPENAI_API_KEY || null;
    this.isEnabled = !!this.apiKey;
    // Use Vite dev proxy to avoid CORS; in production use direct URL
    this.baseUrl = import.meta.env.DEV ? '/openai-proxy/v1' : 'https://api.openai.com/v1';
    
    if (!this.isEnabled) {
      console.warn('⚠️ OpenAI service disabled: VITE_OPENAI_API_KEY not configured');
    }
  }

  /**
   * Check if OpenAI features are available
   */
  isAvailable(): boolean {
    return this.isEnabled;
  }

  /**
   * Chatbot: GPT-4 Mini for user support
   * Context-aware based on user role
   */
  async sendChatMessage(
    messages: ChatMessage[],
    context: ChatbotContext
  ): Promise<string> {
    if (!this.isEnabled) {
      return this.getFallbackChatResponse(context);
    }

    try {
      // Build system prompt based on user type
      const systemPrompt = this.buildChatSystemPrompt(context);
      
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini', // GPT-4 Mini for cost efficiency
          messages: [
            { role: 'system', content: systemPrompt },
            ...messages.map(m => ({ role: m.role, content: m.content }))
          ],
          temperature: 0.7,
          max_tokens: 500
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0]?.message?.content || 'I apologize, but I encountered an issue. Please try again.';
    } catch (error) {
      console.error('Chatbot error:', error);
      return this.getFallbackChatResponse(context);
    }
  }

  /**
   * Generate phishing scenario: GPT-4 Turbo for quality
   */
  async generateScenario(request: ScenarioGenerationRequest): Promise<ScenarioTemplate> {
    if (!this.isEnabled) {
      return this.getFallbackScenario(request);
    }

    try {
      const prompt = this.buildScenarioPrompt(request);

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo', // GPT-4 Turbo for complex scenario generation
          messages: [
            { role: 'system', content: 'You are an expert cybersecurity trainer creating realistic scenarios for security awareness training. You create both legitimate and phishing scenarios to test employees.' },
            { role: 'user', content: prompt }
          ],
          temperature: 0.8,
          max_tokens: 800,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = JSON.parse(data.choices[0]?.message?.content || '{}');
      
      return {
        subject: content.subject,
        sender: content.sender,
        content: content.content,
        redFlags: content.red_flags || [],
        risks: content.risks || [],
        isAiGenerated: true,
        isSafe: !!content.is_safe,
      };
    } catch (error) {
      console.error('Scenario generation error:', error);
      return this.getFallbackScenario(request);
    }
  }

  /**
   * Generate personalized feedback: GPT-4 Mini for speed
   */
  async generateFeedback(request: FeedbackRequest): Promise<FeedbackResponse> {
    if (!this.isEnabled) {
      return this.getFallbackFeedback(request);
    }

    try {
      const prompt = this.buildFeedbackPrompt(request);

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini', // GPT-4 Mini for fast feedback
          messages: [
            { role: 'system', content: 'You are a supportive cybersecurity trainer providing constructive feedback on phishing awareness.' },
            { role: 'user', content: prompt }
          ],
          temperature: 0.6,
          max_tokens: 400,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = JSON.parse(data.choices[0]?.message?.content || '{}');
      
      return {
        explanation: content.explanation || 'Thank you for completing this training.',
        tips: content.tips || [],
        encouragement: content.encouragement || 'Keep up the great work!',
        nextSteps: content.next_steps
      };
    } catch (error) {
      console.error('Feedback generation error:', error);
      return this.getFallbackFeedback(request);
    }
  }

  /**
   * Generate a dynamic quick tip relevant to the threat type
   */
  async generateQuickTip(channel: 'phishing' | 'smishing' | 'vishing', difficulty: 'easy' | 'medium' | 'hard', scenarioHint?: string): Promise<string> {
    if (!this.isEnabled) {
      return this.getFallbackQuickTip(channel);
    }

    try {
      const hintClause = scenarioHint ? ` The current scenario involves: "${scenarioHint}". Make the tip relevant to this scenario.` : '';
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are a cybersecurity trainer. Give ONE concise, unique, actionable security tip. Never repeat common tips. Be specific and practical. Max 2 sentences.' },
            { role: 'user', content: `Give a unique ${difficulty}-level tip about ${channel} attacks that most people don't know. Be specific, avoid generic advice like "check the sender address". Focus on ${difficulty === 'easy' ? 'basic awareness' : difficulty === 'medium' ? 'intermediate detection techniques' : 'advanced threat analysis'}.${hintClause}` }
          ],
          temperature: 1.0,
          max_tokens: 100
        })
      });

      if (!response.ok) throw new Error(`OpenAI API error: ${response.statusText}`);
      const data = await response.json();
      return data.choices[0]?.message?.content || this.getFallbackQuickTip(channel);
    } catch (error) {
      console.error('Quick tip generation error:', error);
      return this.getFallbackQuickTip(channel);
    }
  }

  /**
   * Generate dynamic multiple-choice cybersecurity quiz questions via AI
   */
  async generateQuizQuestion(category: 'phishing' | 'smishing' | 'vishing', questionNumber: number): Promise<{
    question: string;
    options: string[];
    correctIndex: number;
    explanation: string;
    topic: string;
  }> {
    if (!this.isEnabled) {
      return this.getFallbackQuizQuestion(category, questionNumber);
    }

    const topicMap = {
      phishing: 'email security, phishing attacks, email scams, suspicious links, email attachments, sender verification, spoofing',
      smishing: 'SMS scams, text message security, smishing attacks, malicious links in texts, OTP fraud, SIM swapping',
      vishing: 'phone scams, voice phishing, caller ID spoofing, social engineering over phone, tech support scams, impersonation calls',
    };

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are an expert cybersecurity trainer creating quiz questions. Generate a unique multiple-choice question about cybersecurity. Each question should teach something practical. Never repeat the same question. Return valid JSON only.' },
            { role: 'user', content: `Generate a multiple-choice cybersecurity question.

Topic area: ${topicMap[category]}
Question number: ${questionNumber} (use this for variety — pick a different sub-topic each time)

The question should be scenario-based and practical, e.g.:
- "You receive an email from your bank asking you to click a link to verify your account. What should you do?"
- "A text message says you've won a prize and asks you to reply with your address. What's the safest action?"
- "Your colleague asks for your password over the phone because they need urgent access. What do you do?"

Return JSON:
{
  "question": "The question text",
  "options": ["Option A", "Option B", "Option C", "Option D"],
  "correctIndex": 0,
  "explanation": "Brief explanation of why the correct answer is right and what the risk is",
  "topic": "Short topic label e.g. 'Email Links' or 'Password Safety'"
}

RULES:
- Exactly 4 options
- correctIndex is 0-based (0, 1, 2, or 3)
- Only ONE correct answer
- Make wrong answers plausible but clearly wrong to a trained person
- Keep question and options concise` }
          ],
          temperature: 0.95,
          max_tokens: 500,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) throw new Error(`OpenAI API error: ${response.statusText}`);
      const data = await response.json();
      const content = JSON.parse(data.choices[0]?.message?.content || '{}');
      const correctIdx = typeof content.correctIndex === 'number' ? content.correctIndex : 0;
      return {
        question: content.question || 'What should you do if you receive a suspicious email?',
        options: Array.isArray(content.options) && content.options.length === 4
          ? content.options
          : ['Ignore it', 'Report it to IT', 'Click the link to check', 'Forward it to colleagues'],
        correctIndex: correctIdx >= 0 && correctIdx <= 3 ? correctIdx : 0,
        explanation: content.explanation || 'Always report suspicious messages to your IT department.',
        topic: content.topic || 'General Security',
      };
    } catch (error) {
      console.error('Quiz question generation error:', error);
      return this.getFallbackQuizQuestion(category, questionNumber);
    }
  }

  /**
   * Generate dynamic threat demo scenario via AI
   */
  async generateThreatDemo(category: 'phishing' | 'smishing' | 'vishing', scenarioIndex: number): Promise<{
    sender: string;
    email?: string;
    phoneNumber?: string;
    subject?: string;
    body?: string[];
    content?: string;
    transcript?: string;
    callerId?: string;
    buttonText?: string;
    buttons?: string[];
    warning?: string;
    redFlags: Array<{ highlight?: string; text: string }>;
  }> {
    if (!this.isEnabled) {
      return this.getFallbackThreatDemo(category);
    }

    try {
      const promptMap = {
        phishing: `Create a realistic phishing EMAIL demo scenario. Return JSON:
{
  "sender": "displayed sender name",
  "email": "suspicious@fake-domain.com",
  "subject": "email subject with urgency",
  "body": ["paragraph1", "paragraph2", "paragraph3"],
  "buttonText": "Call to action button text →",
  "warning": "threatening/urgent warning text",
  "redFlags": [{"highlight": "suspicious element", "text": "explanation of why it's a red flag"}]
}`,
        smishing: `Create a realistic smishing SMS demo scenario. Return JSON:
{
  "sender": "displayed sender name/number",
  "phoneNumber": "+1-555-XXXX",
  "content": "Full SMS message text with suspicious link",
  "redFlags": [{"highlight": "suspicious element", "text": "explanation of why it's a red flag"}]
}`,
        vishing: `Create a realistic vishing PHONE CALL demo scenario. Return JSON:
{
  "callerId": "Spoofed caller ID name",
  "phoneNumber": "+1-XXX-XXX-XXXX",
  "sender": "Who they claim to be",
  "transcript": "Full call transcript of what the scammer says",
  "buttons": ["Comply with Request", "Hang Up"],
  "redFlags": [{"highlight": "suspicious element", "text": "explanation of why it's a red flag"}]
}`
      };

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are a cybersecurity expert creating realistic threat demonstrations for training. Each scenario must be unique and educational. Return valid JSON only.' },
            { role: 'user', content: `Create scenario #${scenarioIndex + 1} for ${category} threats. Make it different from typical examples - use creative, real-world inspired scenarios.\n\n${promptMap[category]}` }
          ],
          temperature: 1.0,
          max_tokens: 600,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) throw new Error(`OpenAI API error: ${response.statusText}`);
      const data = await response.json();
      return JSON.parse(data.choices[0]?.message?.content || '{}');
    } catch (error) {
      console.error('Threat demo generation error:', error);
      return this.getFallbackThreatDemo(category);
    }
  }

  /**
   * Generate dynamic security tips via AI
   */
  async generateSecurityTips(category: 'phishing' | 'smishing' | 'vishing', count: number = 10): Promise<Array<{ id: number; text: string }>> {
    if (!this.isEnabled) {
      return this.getFallbackSecurityTips(category, count);
    }

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are a cybersecurity expert. Generate unique, actionable security tips. Each tip should be specific and practical - avoid generic, well-known advice. Return valid JSON only.' },
            { role: 'user', content: `Generate ${count} unique ${category} security tips. Mix basic, intermediate, and advanced tips. Include real-world examples where possible.

Return JSON:
{
  "tips": [
    {"id": 1, "text": "Specific, actionable tip with real-world context"}
  ]
}` }
          ],
          temperature: 1.0,
          max_tokens: 1500,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) throw new Error(`OpenAI API error: ${response.statusText}`);
      const data = await response.json();
      const content = JSON.parse(data.choices[0]?.message?.content || '{}');
      return (content.tips || []).map((tip: any, i: number) => ({
        id: tip.id || i + 1,
        text: tip.text || `Security tip ${i + 1}`,
      }));
    } catch (error) {
      console.error('Security tips generation error:', error);
      return this.getFallbackSecurityTips(category, count);
    }
  }

  /**
   * Build role-aware system prompt for chatbot
   */
  private buildChatSystemPrompt(context: ChatbotContext): string {
    const basePrompt = `You are Training Nudge Assistant, a helpful AI supporting cybersecurity awareness training. 
Be concise, friendly, and supportive. Provide actionable advice in 2-3 sentences max.`;

    const roleSpecificGuidance = {
      platform_admin: `
The user is a platform administrator managing multiple organizations.
Help with: system-wide analytics, organization management, deployment strategies, security best practices.`,
      org_admin: `
The user is an organization administrator managing their team's training.
Help with: employee management, simulation campaigns, team analytics, training schedules.`,
      employee: `
The user is a team member participating in security training.
Help with: understanding phishing tactics, simulation questions, improving awareness scores, learning resources.`,
      individual: `
The user is an independent learner using Training Nudge for personal development.
Help with: self-paced learning, understanding threats, improving skills, personal progress tracking.`
    };

    return basePrompt + '\n' + roleSpecificGuidance[context.userType];
  }

  /**
   * Shuffle-bag: draw without replacement until all themes used, then refill.
   * Tracks used indices in sessionStorage per bag key.
   */
  private drawFromShuffleBag(bagKey: string, pool: string[]): string {
    const storageKey = `nudge_shuffle_${bagKey}`;
    let remaining: number[];
    try {
      const stored = sessionStorage.getItem(storageKey);
      remaining = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(remaining)) remaining = [];
    } catch { remaining = []; }

    // Refill if empty
    if (remaining.length === 0) {
      remaining = pool.map((_, i) => i);
      // Fisher-Yates shuffle
      for (let i = remaining.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
      }
    }

    const idx = remaining.pop()!;
    try { sessionStorage.setItem(storageKey, JSON.stringify(remaining)); } catch {}
    return pool[idx];
  }

  /**
   * Build scenario generation prompt - uses 20 unique themes per channel
   */
  private buildScenarioPrompt(request: ScenarioGenerationRequest): string {
    // Phishing themes - 14 per channel
    const emailPhishingThemes = [
      'Account suspension warning from Microsoft 365 due to unusual sign-in',
      'Shared Google Docs document from a colleague requiring login',
      'FedEx/UPS package delivery failure needing address update and small fee',
      'IT department password expiry requiring immediate reset via link',
      'Overdue vendor invoice with payment link attached',
      'CEO urgent wire transfer or gift card request to an employee',
      'Critical Windows security patch requiring immediate download',
      'HR open enrollment deadline closing today with action link',
      'Government tax refund notification with claim link (IRS/HMRC)',
      'Lottery/prize winning notification requiring personal details',
      'LinkedIn recruiter with dream job offer requiring registration',
      'Netflix/Spotify subscription payment failed - update card info',
      'Payroll department requesting updated direct deposit bank details',
      'Company data breach alert urging immediate credential reset',
    ];
    // Safe/legitimate email themes - 6 per channel
    const emailSafeThemes = [
      'Real meeting invite from your manager for a weekly team standup on Microsoft Teams',
      'Genuine IT department announcement about scheduled server maintenance this weekend',
      'Legitimate HR newsletter about upcoming company wellness programme and office events',
      'Real colleague sharing a project update document via official SharePoint link',
      'Authentic company-wide announcement from CEO about quarterly results and new hire',
      'Genuine LinkedIn connection request from a verified colleague in your department',
    ];
    const smsPhishingThemes = [
      'Bank account locked due to suspicious activity - verify now',
      'Amazon gift card prize - claim before expiry',
      'Royal Mail/USPS delivery fee of £1.99 to release held package',
      'HMRC/IRS tax refund available - claim via link',
      'Apple ID password reset code you did not request',
      'Two-factor auth code request impersonating your bank',
      'Unpaid traffic fine - pay within 48 hours or court summons',
      'Electric/gas utility bill overdue - service cutoff imminent',
      'Instagram/Facebook unusual login - verify your identity now',
      'Pre-approved loan offer with incredible rate - apply now',
      'Parking violation fine - pay online to avoid penalty increase',
      'Flight booking change - confirm or lose reservation',
      'Crypto wallet unusual transaction detected - secure account',
      'Medical test results ready - login to patient portal (fake URL)',
    ];
    const smsSafeThemes = [
      'Real two-factor authentication code from your actual bank for a login you just initiated',
      'Genuine delivery confirmation from Amazon/Royal Mail with a real tracking number you expected',
      'Authentic appointment reminder from your dentist or GP surgery for tomorrow',
      'Real notification from your mobile carrier about your monthly bill being ready to view',
      'Genuine text from a friend confirming dinner plans at a specific restaurant tonight',
      'Authentic one-time passcode from Microsoft/Google for a sign-in you just attempted',
    ];
    const phonePhishingThemes = [
      'Bank fraud department about suspicious transaction needing card details',
      'Microsoft tech support detecting malware on your computer',
      'HMRC/IRS threatening arrest for unpaid taxes of thousands',
      'Insurance company calling to verify claim with personal details',
      'Police department with outstanding warrant requiring immediate payment',
      'Internet provider about an outage requiring remote access to router',
      'Extended car warranty final notice about to expire',
      'Aggressive debt collector demanding immediate credit card payment',
      'Investment advisor with exclusive limited-time crypto opportunity',
      'Electric company threatening disconnection in 2 hours unless paid',
      'Social Security Administration about benefits being suspended',
      'Court clerk about missed jury duty with fine or arrest threat',
      'Sweepstakes company - you won but need to pay processing fee',
      'Remote work recruiter needing banking details for payroll setup',
    ];
    const phoneSafeThemes = [
      'Real call from your doctor office confirming an appointment you booked last week',
      'Genuine call from your bank verifying a large purchase YOU just made (they already have details, just confirming)',
      'Authentic call from your child\'s school about an upcoming parent-teacher evening',
      'Real recruiter follow-up call about a job you actually applied for last week',
      'Genuine pharmacy call letting you know your prescription is ready for collection',
      'Authentic call from your car dealership confirming your scheduled MOT/service appointment',
    ];

    const phishingThemes = request.channel === 'email' ? emailPhishingThemes : request.channel === 'sms' ? smsPhishingThemes : phonePhishingThemes;
    const safeThemes = request.channel === 'email' ? emailSafeThemes : request.channel === 'sms' ? smsSafeThemes : phoneSafeThemes;

    // Randomly determine if this scenario is safe (~30% chance) — unpredictable per session
    const num = request.scenarioNumber || 1;
    const isSafe = Math.random() < 0.3;

    let selectedTheme: string;
    if (isSafe) {
      selectedTheme = this.drawFromShuffleBag(`safe_${request.channel}`, safeThemes);
    } else {
      selectedTheme = this.drawFromShuffleBag(`phishing_${request.channel}`, phishingThemes);
    }

    const channelLabel = request.channel === 'email' ? 'email' : request.channel === 'sms' ? 'SMS text message' : 'phone call';

    if (isSafe) {
      return `Generate a completely LEGITIMATE, safe ${channelLabel} for security awareness training.

SPECIFIC TOPIC (you MUST use this theme): ${selectedTheme}
This is scenario #${num} of 20 in a training series.
Difficulty: ${request.difficulty}

IMPORTANT:
- This message is 100% LEGITIMATE and SAFE - it is NOT a scam or phishing attempt
- It should look like a normal, everyday ${channelLabel} that a real person would receive
- Use proper grammar, correct official domains, real-looking formatting
- The sender address/number must be from a genuine, correctly-spelled domain
- Include realistic details like proper signatures, correct branding, and normal tone
- Do NOT include any red flags, urgency tactics, or suspicious elements
- For difficulty "hard": make it look similar to common phishing themes but it IS genuine

Return JSON:
{
  "is_safe": true,
  "subject": "...",
  "sender": "...",
  "content": "...",
  "red_flags": [],
  "risks": []
}`;
    }

    return `Generate a UNIQUE, realistic scam/phishing scenario for a ${channelLabel}.

SPECIFIC TOPIC (you MUST use this theme): ${selectedTheme}
This is scenario #${num} of 20 in a training series - it MUST be completely different from other scenarios.
Difficulty: ${request.difficulty}
Target audience: ${request.targetAudience || 'general'}

Difficulty guide:
- easy: obvious red flags, poor grammar, suspicious formatting
- medium: professional tone but subtle mistakes, more convincing
- hard: very convincing, minimal obvious red flags, sophisticated social engineering

Requirements:
1. Create a realistic sender ${request.channel === 'email' ? 'email address and subject line' : request.channel === 'sms' ? 'phone number and message' : 'caller ID and call transcript'}
2. The content must SPECIFICALLY match the theme above - do not deviate
3. Include 3-4 red flags that are SPECIFIC to this exact scenario (not generic)
4. Never use the word "phishing" in the scenario content
5. List 3 risks specific to this attack type
6. Use a brand, company, or organisation name appropriate to the theme

Return JSON:
{
  "is_safe": false,
  "subject": "...",
  "sender": "...",
  "content": "...",
  "red_flags": [{"type": "...", "description": "..."}],
  "risks": ["..."]
}`;
  }

  /**
   * Build feedback generation prompt
   */
  private buildFeedbackPrompt(request: FeedbackRequest): string {
    const actionContext = request.isCorrect 
      ? `The user correctly ${request.action === 'reported' ? 'reported' : 'identified'} this as a phishing attempt.`
      : `The user ${request.action} the message, which was incorrect. This was a phishing attempt.`;

    return `${actionContext}

Scenario excerpt: "${request.scenarioContent.substring(0, 200)}..."

Provide encouraging feedback as JSON:
{
  "explanation": "Brief explanation of why this was/wasn't correct (1-2 sentences)",
  "tips": ["2-3 actionable tips to improve awareness"],
  "encouragement": "Positive, supportive message",
  "next_steps": "Optional suggestion for what to practice next"
}`;
  }

  /**
   * Fallback responses when OpenAI is not available
   */
  private getFallbackChatResponse(context: ChatbotContext): string {
    const fallbacks = {
      platform_admin: "I'm here to help with platform management. For detailed assistance, please refer to the admin documentation or contact support.",
      org_admin: "I can help with managing your organization's training. Check the Staff Management and Simulations sections for key features.",
      employee: "I'm here to support your cybersecurity training. If you have questions about simulations, check the Resources tab for guides.",
      individual: "Welcome to your personal cybersecurity training! Explore the Dashboard to start your learning journey."
    };
    
    return fallbacks[context.userType];
  }

  private getFallbackScenario(request: ScenarioGenerationRequest): ScenarioTemplate {
    // Pool of realistic fallback scenarios by channel
    const emailScenarios: ScenarioTemplate[] = [
      {
        subject: 'Urgent: Your Account Will Be Suspended',
        sender: 'security-team@micros0ft-support.com',
        content: 'Dear User,\n\nWe have detected unusual sign-in activity on your account. Your account will be suspended within 24 hours unless you verify your identity immediately.\n\nPlease click the link below to verify your account:\nhttps://micros0ft-verify.com/account/secure\n\nIf you do not verify within 24 hours, your account and all associated data will be permanently deleted.\n\nBest regards,\nMicrosoft Security Team',
        redFlags: [
          { type: 'urgency', description: 'Threatens account suspension within 24 hours' },
          { type: 'spoofed_sender', description: 'Domain uses "micros0ft" with a zero instead of "o"' },
          { type: 'suspicious_link', description: 'Link goes to micros0ft-verify.com, not microsoft.com' },
          { type: 'threat', description: 'Threatens permanent data deletion to create panic' }
        ],
        risks: ['Credential theft', 'Account takeover', 'Identity theft'],
        isAiGenerated: false
      },
      {
        subject: 'You have a new document shared with you',
        sender: 'no-reply@sharepointt-online.com',
        content: 'Hi,\n\nJohn Smith has shared a document with you on SharePoint.\n\nDocument: Q4_Financial_Report_CONFIDENTIAL.xlsx\n\nClick here to view: https://sharepointt-online.com/view/doc/28374\n\nThis link will expire in 2 hours.\n\nSharePoint Online Notification',
        redFlags: [
          { type: 'spoofed_sender', description: 'Domain "sharepointt-online.com" has extra "t"' },
          { type: 'urgency', description: 'Link expires in 2 hours to rush the user' },
          { type: 'curiosity', description: 'Uses "CONFIDENTIAL" to entice clicking' },
          { type: 'generic_greeting', description: 'Uses generic "Hi" instead of your name' }
        ],
        risks: ['Credential theft', 'Malware download', 'Data exposure'],
        isAiGenerated: false
      },
      {
        subject: 'Package Delivery Failed - Action Required',
        sender: 'tracking@fedex-deliverynotice.com',
        content: 'Dear Customer,\n\nWe attempted to deliver your package (Tracking #: FX-8847291) but were unable to complete delivery due to an incomplete address.\n\nTo reschedule your delivery, please update your shipping information and pay a small redelivery fee of $1.99:\nhttps://fedex-deliverynotice.com/reschedule/FX-8847291\n\nIf no action is taken within 48 hours, your package will be returned to sender.\n\nFedEx Delivery Services',
        redFlags: [
          { type: 'spoofed_sender', description: 'Not from official fedex.com domain' },
          { type: 'payment_request', description: 'Asks for payment for a redelivery fee' },
          { type: 'urgency', description: '48-hour deadline creates pressure' },
          { type: 'suspicious_link', description: 'Link domain doesn\'t match official FedEx' }
        ],
        risks: ['Credit card theft', 'Financial fraud', 'Identity theft'],
        isAiGenerated: false
      }
    ];

    const smsScenarios: ScenarioTemplate[] = [
      {
        sender: '+1-800-555-0199',
        content: 'ALERT: Your bank account has been locked due to suspicious activity. Verify your identity now to restore access: https://secure-bankverify.com/unlock Reply STOP to opt out.',
        redFlags: [
          { type: 'urgency', description: 'Claims account is locked to create panic' },
          { type: 'suspicious_link', description: 'URL is not an official bank domain' },
          { type: 'generic', description: 'Does not mention which bank or account' },
          { type: 'action_pressure', description: 'Pressures immediate action' }
        ],
        risks: ['Credential theft', 'Financial fraud', 'Account takeover'],
        isAiGenerated: false
      },
      {
        sender: '+1-555-0147',
        content: 'Congratulations! You\'ve won a $500 gift card from Amazon. Claim your prize before it expires: https://amaz0n-rewards.com/claim/gift500 Reply YES to confirm.',
        redFlags: [
          { type: 'too_good', description: 'Unsolicited prize notification' },
          { type: 'spoofed_brand', description: 'URL uses "amaz0n" with a zero' },
          { type: 'urgency', description: 'Implies prize will expire' },
          { type: 'action_pressure', description: 'Asks you to reply to confirm engagement' }
        ],
        risks: ['Personal data theft', 'Financial fraud', 'Malware installation'],
        isAiGenerated: false
      },
      {
        sender: '+1-555-0183',
        content: 'Hi, this is IT Support. We noticed your company email password expires today. Please update it immediately at https://company-passwd-reset.com/update to avoid losing access.',
        redFlags: [
          { type: 'impersonation', description: 'Claims to be IT Support via SMS' },
          { type: 'urgency', description: 'Password "expires today" creates time pressure' },
          { type: 'suspicious_link', description: 'URL is not an official company domain' },
          { type: 'unusual_channel', description: 'Legitimate IT rarely sends password resets via SMS' }
        ],
        risks: ['Credential theft', 'Corporate email compromise', 'Data breach'],
        isAiGenerated: false
      }
    ];

    const phoneScenarios: ScenarioTemplate[] = [
      {
        sender: 'Unknown Number',
        content: 'Hello, I\'m calling from Microsoft Technical Support. We\'ve detected critical malware on your Windows computer that is sending out your personal data. I need you to open your browser and go to remote-help-now.com so our certified technician can remove the threat. This is time-sensitive - if we don\'t act now, your files and banking information could be compromised. Can you go to your computer now?',
        redFlags: [
          { type: 'impersonation', description: 'Claims to be from Microsoft - Microsoft never calls users unsolicited' },
          { type: 'urgency', description: 'Creates panic about malware and data theft' },
          { type: 'remote_access', description: 'Requests remote access to your computer via suspicious URL' },
          { type: 'pressure', description: 'Insists action is time-sensitive to prevent rational thinking' }
        ],
        risks: ['Remote access trojan installation', 'Financial fraud', 'Identity theft'],
        isAiGenerated: false
      },
      {
        sender: 'HMRC',
        content: 'This is an urgent call from Her Majesty\'s Revenue and Customs. Our records indicate you have an outstanding tax liability of £4,892. A warrant for your arrest has been issued and will be executed today unless you make an immediate payment. Press 1 to speak with a settlement officer now, or you will be taken into custody within the next two hours. This is your final notice.',
        redFlags: [
          { type: 'impersonation', description: 'HMRC does not call threatening arrest' },
          { type: 'threat', description: 'Threatens arrest and custody to panic the victim' },
          { type: 'urgency', description: '"Final notice" and "within two hours" to prevent verification' },
          { type: 'payment_demand', description: 'Demands immediate payment over the phone' }
        ],
        risks: ['Financial fraud', 'Payment to criminals', 'Personal information disclosure'],
        isAiGenerated: false
      },
      {
        sender: 'Your Bank',
        content: 'Good afternoon, this is the fraud prevention team at your bank. We\'ve noticed a suspicious transaction of £1,200 on your account just 10 minutes ago. For your protection, we need to verify your identity. Could you please confirm your full card number, the expiry date, and the three-digit security code on the back? We need this to block the transaction before it completes.',
        redFlags: [
          { type: 'data_harvesting', description: 'Asks for full card number, expiry, and CVV - banks never ask for these' },
          { type: 'urgency', description: '10-minute window to create time pressure' },
          { type: 'impersonation', description: 'Claims to be bank fraud team but asks for details banks already have' },
          { type: 'social_engineering', description: 'Uses fear of losing money to override caution' }
        ],
        risks: ['Credit card fraud', 'Account takeover', 'Financial loss'],
        isAiGenerated: false
      }
    ];

    // Safe / legitimate fallback scenarios
    const safeEmailScenarios: ScenarioTemplate[] = [
      {
        subject: 'Team Standup Moved to 10:30 AM Tomorrow',
        sender: 'sarah.jenkins@company.com',
        content: 'Hi team,\n\nJust a quick heads-up - tomorrow\'s standup has been moved from 9:30 AM to 10:30 AM to accommodate the client call.\n\nSame Teams link as usual. Agenda will be the sprint review items we discussed on Friday.\n\nThanks,\nSarah Jenkins\nProject Manager\nExt. 4821',
        redFlags: [],
        risks: [],
        isAiGenerated: false,
        isSafe: true,
      },
      {
        subject: 'Server Maintenance Notice - Saturday 2 AM to 6 AM',
        sender: 'it-operations@company.com',
        content: 'Dear all,\n\nPlanned maintenance window this Saturday (15 March) from 02:00 to 06:00 GMT.\n\nAffected services: Email, SharePoint, internal wiki.\nNo action required from your side - all services will be restored automatically.\n\nIf you experience issues after 06:00, please contact the IT helpdesk on ext. 5000 or helpdesk@company.com.\n\nRegards,\nIT Operations Team',
        redFlags: [],
        risks: [],
        isAiGenerated: false,
        isSafe: true,
      },
    ];

    const safeSmsScenarios: ScenarioTemplate[] = [
      {
        sender: 'Royal Mail',
        content: 'Your parcel (tracking: RM7294815GB) was delivered to your front porch at 14:32 today. Track: https://www.royalmail.com/track/RM7294815GB',
        redFlags: [],
        risks: [],
        isAiGenerated: false,
        isSafe: true,
      },
      {
        sender: 'NHS',
        content: 'Reminder: You have an appointment at Riverside Medical Centre on 18 Mar at 10:15 AM with Dr. Patterson. Reply YES to confirm or call 020 7946 0958 to reschedule.',
        redFlags: [],
        risks: [],
        isAiGenerated: false,
        isSafe: true,
      },
    ];

    const safePhoneScenarios: ScenarioTemplate[] = [
      {
        sender: 'Dr. Patterson\'s Office',
        content: 'Hello, this is reception at Riverside Medical Centre calling to confirm your appointment tomorrow at 10:15 AM with Dr. Patterson. We just need to check - are you still able to attend? If you need to reschedule, we have availability on Thursday afternoon. Also, please remember to bring your repeat prescription form.',
        redFlags: [],
        risks: [],
        isAiGenerated: false,
        isSafe: true,
      },
      {
        sender: 'Kwik Fit Service Centre',
        content: 'Good morning, this is Kwik Fit in Bromley calling about your car service booked for Friday at 9 AM. We just wanted to confirm the booking and let you know we\'ve ordered the brake pads you need. The total estimate is £185 as we discussed. Is Friday still good for you?',
        redFlags: [],
        risks: [],
        isAiGenerated: false,
        isSafe: true,
      },
    ];

    // Determine if this should be a safe scenario (same indices as AI: 3,7,10,13,17,20)
    const num = request.scenarioNumber || 1;
    const safeIndices = [3, 7, 10, 13, 17, 20];
    const isSafe = safeIndices.includes(num);

    if (isSafe) {
      const safePool = request.channel === 'email' ? safeEmailScenarios : request.channel === 'sms' ? safeSmsScenarios : safePhoneScenarios;
      const idx = safeIndices.indexOf(num) % safePool.length;
      return safePool[idx];
    }

    const pool = request.channel === 'email' ? emailScenarios : request.channel === 'sms' ? smsScenarios : phoneScenarios;
    const phishingNums = Array.from({ length: 20 }, (_, i) => i + 1).filter(n => !safeIndices.includes(n));
    const phishIdx = phishingNums.indexOf(num);
    const index = (phishIdx >= 0 ? phishIdx : (num - 1)) % pool.length;
    return pool[index];
  }

  private getFallbackFeedback(request: FeedbackRequest): FeedbackResponse {
    if (request.isCorrect) {
      return {
        explanation: 'Great job identifying this threat!',
        tips: [
          'Always verify sender addresses',
          'Look for urgency tactics',
          'Check links before clicking'
        ],
        encouragement: 'You\'re building strong security awareness!',
      };
    } else {
      return {
        explanation: 'This was a phishing attempt. Let\'s learn from it.',
        tips: [
          'Watch for suspicious sender addresses',
          'Be cautious of urgent requests',
          'Verify unexpected messages independently'
        ],
        encouragement: 'Every mistake is a learning opportunity. You\'re improving!',
      };
    }
  }

  private getFallbackQuickTip(channel: 'phishing' | 'smishing' | 'vishing'): string {
    const tipPools: Record<string, string[]> = {
      phishing: [
        'Phishing emails often arrive at unusual times - late night or early morning sends from "corporate" addresses are a red flag.',
        'Check if the reply-to address differs from the sender - attackers often use one address to send but collect replies elsewhere.',
        'Hover over any button before clicking - the actual URL is often completely different from what the button text suggests.',
        'If an email contains a QR code asking you to "verify" something, it\'s almost certainly a phishing attempt.',
        'Legitimate password reset emails only come after YOU request them - unsolicited ones are always suspicious.',
        'Check the email\'s SPF/DKIM headers by viewing the full message source - spoofed emails usually fail these checks.',
        'Be wary of emails that address you by your email address instead of your name - it means they\'re mass-targeting.',
        'If a "security alert" email doesn\'t reference your specific account details, it\'s likely phishing.',
      ],
      smishing: [
        'Banks will never send you a text with a link to "verify" your account - they\'ll ask you to call the number on your card.',
        'Scam texts often use URL shorteners (bit.ly, tinyurl) to hide suspicious destination addresses.',
        'If you receive a delivery notification via SMS but haven\'t ordered anything, don\'t click - check the retailer\'s app directly.',
        'Watch for texts with slight misspellings of brand names like "Amaz0n" or "PayPa1" - these bypass spam filters.',
        'Legitimate companies don\'t ask you to "reply YES" to confirm anything via text - that\'s a social engineering tactic.',
        'International phone numbers sending "local" service messages are a clear smishing indicator.',
        'If a text claims your "account is locked" but uses a generic greeting, it\'s been sent to thousands of people simultaneously.',
      ],
      vishing: [
        'Scammers can spoof any caller ID - seeing your bank\'s name on the screen doesn\'t mean it\'s actually them calling.',
        'If someone calls claiming to be from tech support, hang up and call the company\'s official number to verify.',
        'Real government agencies (HMRC, IRS) will never threaten arrest or demand payment over the phone.',
        'Watch for "can you hear me?" calls - scammers record your "yes" to authorize fraudulent transactions.',
        'If a caller asks you to stay on the line while they "transfer you," they may be keeping the line open to intercept your next call.',
        'Legitimate companies will never ask you to download remote access software (TeamViewer, AnyDesk) during a phone call.',
        'If a caller creates urgency by saying "don\'t hang up or your account will be closed," that pressure tactic itself is the red flag.',
      ],
    };
    const pool = tipPools[channel];
    return pool[Math.floor(Math.random() * pool.length)];
  }

  private getFallbackQuizQuestion(category: 'phishing' | 'smishing' | 'vishing', questionNumber: number): {
    question: string; options: string[]; correctIndex: number; explanation: string; topic: string;
  } {
    const pools: Record<string, Array<{ question: string; options: string[]; correctIndex: number; explanation: string; topic: string }>> = {
      phishing: [
        { question: 'You receive an email from your bank asking you to click a link to verify your account details. What should you do?', options: ['Click the link and enter your details', 'Reply to the email asking if it\'s real', 'Go directly to your bank\'s website or app to check', 'Forward it to all your contacts as a warning'], correctIndex: 2, explanation: 'Never click links in unsolicited emails. Always navigate to the official website directly by typing the URL or using the official app.', topic: 'Email Links' },
        { question: 'What is the most reliable way to verify whether an email is genuinely from a company?', options: ['Check if the email has a company logo', 'Look at the sender\'s full email address and domain', 'See if the email looks professional', 'Check if it was sent during business hours'], correctIndex: 1, explanation: 'Logos and formatting can be copied easily. The sender\'s full email domain is the most reliable indicator — legitimate companies use their official domain.', topic: 'Sender Verification' },
        { question: 'An email from "IT Support" asks you to download an attachment to update your security software. What\'s the safest action?', options: ['Download and run it immediately', 'Check with your IT department through a separate channel', 'Scan it with antivirus then open it', 'Open it in a virtual machine'], correctIndex: 1, explanation: 'Always verify unexpected requests through a separate communication channel. Contact your IT department directly — don\'t trust the contact details in the suspicious email.', topic: 'Suspicious Attachments' },
        { question: 'Which of these is a common red flag in a phishing email?', options: ['The email is addressed to you by name', 'The email contains a company logo', 'The email creates a sense of urgency or threatens consequences', 'The email was sent during working hours'], correctIndex: 2, explanation: 'Phishing emails often create artificial urgency ("Your account will be locked in 24 hours!") to pressure you into acting before thinking critically.', topic: 'Red Flags' },
      ],
      smishing: [
        { question: 'You receive a text saying "Your parcel couldn\'t be delivered. Pay a £1.50 fee here: [link]". What should you do?', options: ['Pay the small fee to get your parcel', 'Click the link to check the tracking details', 'Delete the message and check the courier\'s official website', 'Reply STOP to unsubscribe'], correctIndex: 2, explanation: 'Delivery fee scams are extremely common. Legitimate couriers don\'t ask for payments via text links. Check the official website or app directly.', topic: 'Delivery Scams' },
        { question: 'A text from your "bank" says unusual activity was detected and asks you to call a number in the message. What do you do?', options: ['Call the number immediately to protect your account', 'Ignore it — banks never send texts', 'Call your bank using the number on the back of your card or their official website', 'Reply with your account number so they can check'], correctIndex: 2, explanation: 'Never call numbers provided in suspicious texts. Use the official number from your bank card or website to verify any security alerts.', topic: 'Bank Impersonation' },
        { question: 'What makes SMS phishing (smishing) particularly effective compared to email phishing?', options: ['Text messages can contain more content', 'People tend to trust texts more and read them quickly', 'SMS has better encryption', 'Phone numbers are harder to spoof'], correctIndex: 1, explanation: 'People are more likely to trust and quickly act on text messages. The short format also makes it harder to spot red flags compared to emails.', topic: 'Smishing Awareness' },
        { question: 'You get a text with a one-time code you didn\'t request, followed by a message from someone claiming to be from the service asking you to share it. What do you do?', options: ['Share the code since they seem to know about it', 'Ignore both messages and secure your account', 'Reply asking them to verify their identity', 'Share half the code as a test'], correctIndex: 1, explanation: 'This is a common account takeover technique. Never share one-time codes with anyone. If you didn\'t request it, someone may be trying to access your account — change your password immediately.', topic: 'OTP Fraud' },
      ],
      vishing: [
        { question: 'Someone calls claiming to be from Microsoft saying your computer has a virus. They ask for remote access. What should you do?', options: ['Let them connect — Microsoft watches all computers', 'Ask them to prove they\'re from Microsoft', 'Hang up immediately — Microsoft never cold-calls about viruses', 'Give them access but watch what they do'], correctIndex: 2, explanation: 'Microsoft and other tech companies never make unsolicited calls about computer problems. This is one of the most common tech support scams.', topic: 'Tech Support Scams' },
        { question: 'A caller claims to be from HMRC and says you\'ll be arrested if you don\'t pay a tax debt immediately. What do you do?', options: ['Pay immediately to avoid arrest', 'Ask for their badge number to verify', 'Hang up — HMRC never threatens arrest over the phone', 'Transfer to a smaller amount to stall them'], correctIndex: 2, explanation: 'Government agencies never threaten immediate arrest over the phone or demand instant payment. This is a fear-based social engineering tactic.', topic: 'Government Impersonation' },
        { question: 'Your "bank" calls and asks you to transfer money to a "safe account" because of fraud on your account. What\'s the correct response?', options: ['Transfer the money quickly to protect it', 'Ask them to send confirmation by email first', 'Hang up and call your bank on their official number', 'Give them your account details to verify'], correctIndex: 2, explanation: 'Banks will never ask you to transfer money to a "safe account". This is a well-known social engineering tactic. Always hang up and call back on the number from your card.', topic: 'Safe Account Scams' },
        { question: 'What is "caller ID spoofing"?', options: ['When a caller uses a loud voice to seem authoritative', 'When a scammer makes their phone number appear as a trusted organisation', 'When someone calls from a blocked number', 'When a call is routed through multiple countries'], correctIndex: 1, explanation: 'Scammers can manipulate caller ID to display any number — including your bank\'s real number. Never trust caller ID alone to verify a caller\'s identity.', topic: 'Caller ID Spoofing' },
      ],
    };
    const pool = pools[category] || pools.phishing;
    return pool[questionNumber % pool.length];
  }

  private getFallbackThreatDemo(category: 'phishing' | 'smishing' | 'vishing'): any {
    if (category === 'phishing') {
      const pool = [
        { sender: 'HR Department', email: 'hr-notifications@company-portal.net', subject: '📋 Annual Benefits Enrollment Closing Today', body: ['Dear Team Member,', 'This is your final reminder that the annual benefits enrollment window closes TODAY at 5:00 PM. If you have not selected your benefits package, you will automatically be enrolled in the basic plan.', 'To review and select your benefits, click below immediately.'], buttonText: 'Select Benefits Now →', warning: 'You will lose your current benefits if you don\'t act before 5:00 PM today.', redFlags: [{ highlight: 'company-portal.net', text: 'Not an official company domain - real HR uses internal systems.' }, { highlight: 'TODAY at 5:00 PM', text: 'Extreme urgency designed to prevent you from thinking critically.' }, { text: 'Generic "Dear Team Member" instead of your actual name.' }] },
        { sender: 'CEO Office', email: 'ceo-urgent@company-comms.org', subject: '🔒 Confidential: Salary Review Results', body: ['Hi,', 'The annual salary review results are now available. As this information is confidential, please download the encrypted PDF using the secure link below.', 'You will need to enter your company credentials to decrypt the file.'], buttonText: 'Download Salary Review PDF →', warning: 'This document will be available for 24 hours only.', redFlags: [{ highlight: 'company-comms.org', text: 'Fake domain designed to look official.' }, { highlight: 'enter your company credentials', text: 'Credential harvesting - legitimate documents don\'t require re-authentication.' }, { text: 'Uses curiosity about salary to lure clicks.' }] },
      ];
      return pool[Math.floor(Math.random() * pool.length)];
    } else if (category === 'smishing') {
      const pool = [
        { sender: 'Amazon Delivery', phoneNumber: '+1-206-555-0188', content: 'Amazon: Your package couldn\'t be delivered due to an incorrect address. Update your delivery details here: https://amaz0n-delivery.com/update or your package will be returned by tomorrow.', redFlags: [{ highlight: 'amaz0n-delivery.com', text: 'Fake domain using "0" instead of "o" - not amazon.com.' }, { highlight: 'returned by tomorrow', text: 'Creates artificial time pressure.' }, { text: 'Amazon sends delivery updates through their app, not SMS links.' }] },
        { sender: 'Revenue Service', phoneNumber: '+44-800-555-0142', content: 'HMRC ALERT: You have an outstanding tax refund of £847.20. Claim within 48hrs or it will be forfeit: https://hmrc-refund-claim.co.uk/verify', redFlags: [{ highlight: 'hmrc-refund-claim.co.uk', text: 'Not the real HMRC domain (gov.uk).' }, { highlight: '£847.20', text: 'Specific amount makes it seem real but is a lure.' }, { text: 'HMRC never sends refund links via SMS.' }] },
      ];
      return pool[Math.floor(Math.random() * pool.length)];
    } else {
      const pool = [
        { callerId: 'Bank Fraud Department', phoneNumber: '+44-800-555-0199', sender: 'Bank Fraud Investigator', transcript: 'Hello, this is the fraud department at your bank. We\'ve detected a suspicious transaction of £2,450 being processed on your account right now. To stop this payment going through, I need to verify your identity. Can you please confirm your date of birth, the last four digits of your card, and your online banking security code?', buttons: ['Give Information', 'Hang Up'], redFlags: [{ highlight: 'confirm your date of birth', text: 'Real banks never ask you to verify identity by providing security details over an incoming call.' }, { highlight: '£2,450 being processed right now', text: 'Creates panic so you act without thinking.' }, { text: 'Legitimate fraud teams tell you to hang up and call the number on your card.' }] },
        { callerId: 'Microsoft Technical Support', phoneNumber: '+1-425-555-0173', sender: 'Microsoft Support Technician', transcript: 'Good afternoon, this is Microsoft Technical Support. Our system has detected that your computer has been infected with malware and is currently being used to send spam. I need to remotely access your computer to remove the threat. Can you please go to anydesk.com and download our remote support tool?', buttons: ['Download Software', 'Hang Up'], redFlags: [{ highlight: 'remotely access your computer', text: 'Microsoft never cold-calls users to fix their computers.' }, { highlight: 'anydesk.com', text: 'Legitimate support never asks you to download third-party remote access tools.' }, { text: 'Tech support scams are one of the most common vishing attacks.' }] },
      ];
      return pool[Math.floor(Math.random() * pool.length)];
    }
  }

  private getFallbackSecurityTips(category: 'phishing' | 'smishing' | 'vishing', count: number): Array<{ id: number; text: string }> {
    const pools: Record<string, string[]> = {
      phishing: [
        'Always check the sender\'s full email address - not just the display name. Scammers use lookalike domains.',
        'Hover over links before clicking to preview the actual URL. If it looks suspicious, don\'t click.',
        'Be wary of emails that create urgency - "Act now!" or "Your account will be suspended" are red flags.',
        'Never enter your password on a page you reached via an email link. Navigate to the site directly.',
        'If an email asks you to download an unexpected attachment, verify with the sender first.',
        'Legitimate companies will never ask for your password or full card number via email.',
        'Enable multi-factor authentication on all accounts - it stops most phishing even if passwords are stolen.',
        'Be sceptical of colleague emails asking for gift cards or wire transfers - verify by phone.',
        'QR code phishing ("quishing") is rising - don\'t scan QR codes from unexpected emails.',
        'Use a password manager - it won\'t auto-fill credentials on fake lookalike sites.',
      ],
      smishing: [
        'Banks never send texts with links to "verify" your account - call the number on your card instead.',
        'Scam texts often use URL shorteners to hide suspicious destination addresses.',
        'Delivery notification texts when you haven\'t ordered anything are almost always smishing.',
        'Watch for texts with misspellings of brand names like "Amaz0n" - these bypass spam filters.',
        'Legitimate companies don\'t ask you to "reply YES" via text - that\'s social engineering.',
        'International numbers sending "local" service messages are a clear smishing indicator.',
        'If a text claims your "account is locked" but uses a generic greeting, it\'s mass-targeted.',
        'Never call a number provided in a suspicious text - look up the organisation\'s real number.',
        'Prize/gift card texts are always scams - legitimate companies don\'t give away money via SMS.',
        'Forward suspicious texts to 7726 (SPAM) to report them to your mobile carrier.',
      ],
      vishing: [
        'Scammers can spoof any caller ID - seeing your bank\'s name doesn\'t mean it\'s really them.',
        'If someone calls claiming to be tech support, hang up and call the company\'s official number.',
        'Government agencies never threaten arrest or demand payment over the phone.',
        'Watch for "can you hear me?" calls - scammers record your "yes" for fraudulent authorizations.',
        'If a caller asks you to stay on the line during a "transfer," they may be intercepting your next call.',
        'Legitimate companies never ask you to download remote access tools during a phone call.',
        'If a caller creates urgency ("don\'t hang up!"), that pressure itself is the red flag.',
        'Always verify unexpected callers by hanging up and calling back on a known, official number.',
        'Be wary of callers who already know some of your personal details - this builds false trust.',
        'If someone asks you to move money to a "safe account," it\'s always a scam.',
      ],
    };
    const pool = pools[category];
    // Shuffle and return requested count
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count).map((text, i) => ({ id: i + 1, text }));
  }

  /**
   * Generate a dynamic reflection quiz based on the scenario just completed
   */
  async generateReflectionQuiz(channel: string, scenarioContent: string): Promise<{
    question: string;
    options: { text: string; isCorrect: boolean; explanation?: string }[];
    remember: string;
  }> {
    if (!this.isEnabled) {
      return this.getFallbackReflectionQuiz(channel);
    }

    try {
      const channelLabel = channel === 'email' ? 'email phishing' : channel === 'sms' ? 'SMS smishing' : 'phone vishing';
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are a cybersecurity trainer creating unique, varied quiz questions. Never repeat the same question structure. Return valid JSON only.' },
            { role: 'user', content: `Session seed: ${Date.now()}-${Math.random().toString(36).slice(2, 8)}

The user just completed a ${channelLabel} training scenario. Scenario excerpt: "${scenarioContent.substring(0, 300)}"

Generate a UNIQUE multiple-choice quiz question about ${channelLabel} threats. Requirements:
- Make it SPECIFIC to the scenario excerpt above
- Vary the question angle: sometimes ask about red flags, sometimes about the correct response, sometimes about WHY a tactic works, sometimes about what the attacker was trying to achieve
- Randomise where the correct answer appears (A, B, or C)
- Do NOT reuse common generic questions like "what is phishing" or "what should you do"

Return JSON:
{
  "question": "A specific, scenario-relevant question",
  "options": [
    {"text": "A. ...", "isCorrect": false},
    {"text": "B. ...", "isCorrect": true, "explanation": "Brief explanation of why this is correct"},
    {"text": "C. ...", "isCorrect": false}
  ],
  "remember": "One-sentence key takeaway specific to this scenario type"
}` }
          ],
          temperature: 0.95,
          max_tokens: 400,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) throw new Error('API error');
      const data = await response.json();
      const content = JSON.parse(data.choices[0]?.message?.content || '{}');
      if (content.question && content.options && content.remember) return content;
      return this.getFallbackReflectionQuiz(channel);
    } catch {
      return this.getFallbackReflectionQuiz(channel);
    }
  }

  /**
   * Generate dynamic learning tips based on the scenario
   */
  async generateLearningTips(channel: string, scenarioContent: string): Promise<string[]> {
    if (!this.isEnabled) {
      return this.getFallbackLearningTips(channel);
    }

    try {
      const channelLabel = channel === 'email' ? 'email' : channel === 'sms' ? 'SMS' : 'phone call';
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are a cybersecurity trainer. Provide varied, practical advice. Return valid JSON only.' },
            { role: 'user', content: `Session seed: ${Date.now()}-${Math.random().toString(36).slice(2, 8)}

Based on this ${channelLabel} phishing scenario: "${scenarioContent.substring(0, 300)}"

Generate 4 SPECIFIC, actionable learning tips for this type of attack. Requirements:
- Each tip must have a **bold lead phrase** followed by a practical explanation
- Make them specific to THIS scenario - not generic cybersecurity advice
- Vary the advice angles: detection clues, correct response actions, psychological tactics used, real-world context

Return JSON:
{"tips": ["**Bold lead phrase** followed by specific advice"]}` }
          ],
          temperature: 0.9,
          max_tokens: 400,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) throw new Error('API error');
      const data = await response.json();
      const content = JSON.parse(data.choices[0]?.message?.content || '{}');
      if (Array.isArray(content.tips) && content.tips.length >= 4) return content.tips.slice(0, 4);
      return this.getFallbackLearningTips(channel);
    } catch {
      return this.getFallbackLearningTips(channel);
    }
  }

  private getFallbackReflectionQuiz(channel: string): { question: string; options: { text: string; isCorrect: boolean; explanation?: string }[]; remember: string } {
    const quizPools: Record<string, Array<{ question: string; options: { text: string; isCorrect: boolean; explanation?: string }[]; remember: string }>> = {
      email: [
        { question: 'What is the most reliable way to verify a suspicious email?', options: [{ text: 'A. Reply asking if it is real', isCorrect: false }, { text: 'B. Contact the company directly via their official website', isCorrect: true, explanation: 'Always verify independently through official channels - never use contact info from the suspicious email itself' }, { text: 'C. Forward it to a colleague', isCorrect: false }], remember: 'Never trust links or contact info within a suspicious email - verify independently through official channels.' },
        { question: 'Which detail is hardest for scammers to convincingly fake?', options: [{ text: 'A. The display name of the sender', isCorrect: false }, { text: 'B. The email subject line', isCorrect: false }, { text: 'C. The actual domain after the @ in the sender address', isCorrect: true, explanation: 'Display names are trivial to fake, but registering convincing domains takes effort and often has typos' }], remember: 'Always check the full email address domain - not just the display name.' },
        { question: 'Why do phishing emails often include tight deadlines?', options: [{ text: 'A. Because the offer genuinely expires soon', isCorrect: false }, { text: 'B. To pressure you into acting without thinking critically', isCorrect: true, explanation: 'Urgency is a core social engineering tactic - real companies give reasonable timeframes' }, { text: 'C. To comply with data protection regulations', isCorrect: false }], remember: 'Artificial urgency is a manipulation tactic - legitimate organisations give you time to respond.' },
        { question: 'An email tells you to "update your credit card on file or lose access." What is the safest response?', options: [{ text: 'A. Click the link and update your card quickly', isCorrect: false }, { text: 'B. Navigate to the service\'s website directly and check your account there', isCorrect: true, explanation: 'If the request is real, you\'ll see it when you log in through the official site - never use a link from the email' }, { text: 'C. Ignore it completely', isCorrect: false }], remember: 'When in doubt, go directly to the website - never rely on links inside emails.' },
        { question: 'What makes a "shared document" email suspicious?', options: [{ text: 'A. The document is a spreadsheet', isCorrect: false }, { text: 'B. You weren\'t expecting it and the sharing domain has typos', isCorrect: true, explanation: 'Unexpected document shares with misspelled domains are a classic phishing technique' }, { text: 'C. It arrives during business hours', isCorrect: false }], remember: 'Unexpected shared documents from unfamiliar or misspelled domains should always be verified before clicking.' },
        { question: 'What should you check FIRST when you receive an unexpected email with a link?', options: [{ text: 'A. Whether the email looks visually professional', isCorrect: false }, { text: 'B. The sender\'s email address and where the link actually leads', isCorrect: true, explanation: 'Hover over links to see the real URL - scammers invest in making emails look real, so visual appearance alone isn\'t reliable' }, { text: 'C. Whether your colleagues received the same email', isCorrect: false }], remember: 'Always inspect the sender address and hover over links before taking any action.' },
      ],
      sms: [
        { question: 'Your bank texts asking you to click a link. What should you do?', options: [{ text: 'A. Click the link since it might be urgent', isCorrect: false }, { text: 'B. Ignore it and log into your banking app directly', isCorrect: true, explanation: 'Banks never send action links via SMS - always navigate to the app or website yourself' }, { text: 'C. Reply STOP to unsubscribe', isCorrect: false }], remember: 'Banks never ask you to click links in text messages - use the official app instead.' },
        { question: 'Which is the biggest red flag in a delivery notification SMS?', options: [{ text: 'A. A request to pay a small fee via a link', isCorrect: true, explanation: 'Legitimate couriers never charge fees via SMS links - delivery fees are handled at purchase or at the door' }, { text: 'B. The message arriving outside business hours', isCorrect: false }, { text: 'C. The message being short', isCorrect: false }], remember: 'If a delivery text asks for payment via a link, it is always a scam.' },
        { question: 'Why do smishing texts often use URL shorteners?', options: [{ text: 'A. To save character space in the SMS', isCorrect: false }, { text: 'B. To hide the actual suspicious destination URL', isCorrect: true, explanation: 'Shortened URLs mask the real domain so you cannot see where the link actually leads' }, { text: 'C. Because legitimate companies prefer them', isCorrect: false }], remember: 'Shortened URLs in unexpected texts are a red flag - they hide the true destination.' },
        { question: 'You receive a text: "Your 2FA code is 847291. If you did not request this, click here." What should you do?', options: [{ text: 'A. Click the link to secure your account', isCorrect: false }, { text: 'B. Change your password using the official app - do NOT click the link', isCorrect: true, explanation: 'If you didn\'t request a 2FA code, someone may have your password. Change it through official channels, never through a text link.' }, { text: 'C. Reply CANCEL to the text', isCorrect: false }], remember: 'Unrequested 2FA codes mean someone may have your password - change it through official channels immediately.' },
        { question: 'How can you tell a legitimate company text from a scam?', options: [{ text: 'A. Legitimate ones never contain links', isCorrect: false }, { text: 'B. Legitimate ones come from short codes or verified sender names, not random mobile numbers', isCorrect: true, explanation: 'Real companies use official short codes or alpha sender IDs, not +44 7XXX numbers' }, { text: 'C. Legitimate texts are always longer', isCorrect: false }], remember: 'Check the sender - real companies use verified sender names or short codes, not random phone numbers.' },
        { question: 'A text says you\'ve won a competition. You didn\'t enter one. What does this tell you?', options: [{ text: 'A. You were randomly selected', isCorrect: false }, { text: 'B. It is almost certainly a scam - you cannot win a prize you never entered', isCorrect: true, explanation: 'A fundamental rule: you cannot win competitions you never entered' }, { text: 'C. It could be real if the prize is small', isCorrect: false }], remember: 'You cannot win a competition you never entered - unsolicited prize messages are always scams.' },
      ],
      phone: [
        { question: 'A caller claims to be from your bank and asks for your card PIN. What should you do?', options: [{ text: 'A. Provide it since they might need to verify', isCorrect: false }, { text: 'B. Hang up and call the number on the back of your card', isCorrect: true, explanation: 'Your bank will NEVER ask for your PIN over the phone - always hang up and call back on a verified number' }, { text: 'C. Give only the last two digits', isCorrect: false }], remember: 'No legitimate organisation will ever ask for your PIN or full password over the phone.' },
        { question: 'Why can you not trust caller ID to verify who is calling?', options: [{ text: 'A. Phone networks are unreliable', isCorrect: false }, { text: 'B. Scammers can spoof any caller ID to display any name or number', isCorrect: true, explanation: 'Caller ID spoofing technology allows scammers to show any number they want - even your own bank\'s' }, { text: 'C. Caller ID only works for mobile numbers', isCorrect: false }], remember: 'Caller ID can be spoofed - never trust it as proof of identity.' },
        { question: 'What is a common sign that a phone call is a vishing attack?', options: [{ text: 'A. The caller identifies themselves by name', isCorrect: false }, { text: 'B. The caller asks you to download remote access software', isCorrect: true, explanation: 'Legitimate support never asks you to install TeamViewer, AnyDesk, or similar tools during unsolicited calls' }, { text: 'C. The call comes during business hours', isCorrect: false }], remember: 'Never download remote access software when asked by an unsolicited caller.' },
        { question: 'A caller threatens arrest if you don\'t pay immediately. What should you do?', options: [{ text: 'A. Ask how to pay to avoid arrest', isCorrect: false }, { text: 'B. Hang up - government agencies never threaten arrest over the phone and demand immediate payment', isCorrect: true, explanation: 'HMRC, police, and courts issue official letters - they never demand payment over the phone with threats' }, { text: 'C. Ask for their badge number to verify', isCorrect: false }], remember: 'Government agencies never call threatening arrest and demanding immediate payment - that is always a scam.' },
        { question: 'A caller says they\'re from IT support and your computer is infected. What gives it away as a scam?', options: [{ text: 'A. They called you - legitimate IT support does not make unsolicited calls about infections', isCorrect: true, explanation: 'Microsoft, Apple, and your ISP will never call you out of the blue about malware on your device' }, { text: 'B. They have a foreign accent', isCorrect: false }, { text: 'C. They called on a weekday', isCorrect: false }], remember: 'No tech company will ever call you unsolicited about viruses - these calls are always scams.' },
        { question: 'What is the safest way to verify a caller claiming to be from your bank?', options: [{ text: 'A. Ask them to prove it by reading your account balance', isCorrect: false }, { text: 'B. Hang up, wait 5 minutes, then call the number printed on your bank card', isCorrect: true, explanation: 'A 5-minute wait ensures the scammer\'s call is fully disconnected before you dial the real number' }, { text: 'C. Ask them security questions about YOUR account', isCorrect: false }], remember: 'Always hang up and call back on the official number from your card or statement - wait at least 5 minutes.' },
      ],
    };
    const pool = quizPools[channel] || quizPools.email;
    // Use timestamp-based index for more variation between sessions
    const idx = (Date.now() % (pool.length * 7)) % pool.length;
    return pool[idx];
  }

  private getFallbackLearningTips(channel: string): string[] {
    const tipPools: Record<string, string[][]> = {
      email: [
        ['**Check the sender\'s full email domain** - look past the display name for misspelled or look-alike domains', '**Hover over links before clicking** - the actual URL often differs from the visible text', '**Be sceptical of urgency** - legitimate companies give you reasonable time to act', '**Report suspicious emails** to your IT or security team instead of just deleting them'],
        ['**Never enter credentials from an email link** - type the website address directly in your browser', '**Watch for generic greetings** like "Dear User" - real companies usually address you by name', '**Verify unexpected attachments** by contacting the sender through a separate channel', '**Enable multi-factor authentication** - it protects you even if your password is compromised'],
        ['**Look for spelling and grammar errors** - many phishing emails contain subtle mistakes that legitimate companies wouldn\'t make', '**Be cautious of "too good to be true" offers** - unexpected refunds, prizes, or deals are classic bait', '**Check if the email was expected** - were you anticipating this message, or did it arrive out of the blue?', '**Verify with colleagues** - if an internal-looking email seems off, check with the supposed sender via a different channel'],
        ['**Inspect email headers for routing anomalies** - emails claiming to be from your company but routed through external servers are suspect', '**Never download .exe or .zip attachments** from unexpected emails - these are common malware delivery methods', '**Watch for brand impersonation** - scammers copy logos and formatting but the domain always reveals the truth', '**Trust your instincts** - if something feels "off" about an email, report it rather than risk clicking'],
      ],
      sms: [
        ['**Never click links in unexpected texts** - open the company\'s app directly instead', '**Verify delivery texts** by checking your order history in the retailer\'s official app', '**Watch for urgency tactics** - "act within 24 hours" is a social engineering pressure technique', '**Report suspicious texts** by forwarding to 7726 (SPAM) on most carriers'],
        ['**Banks never send action links via SMS** - if in doubt, call the number on your card', '**Check the sender number** - legitimate businesses use short codes, not random mobile numbers', '**Be wary of unsolicited prizes** - you cannot win a competition you never entered', '**Don\'t reply to suspicious texts** - even STOP replies confirm your number is active'],
        ['**Watch for impersonation of delivery services** - Royal Mail, DPD, and Evri don\'t ask for payments via text links', '**Never share one-time codes received by SMS** - these are your security codes, not the caller\'s', '**Be suspicious of texts from international numbers** claiming to be UK services', '**Check the URL carefully** - scam texts often use domains like royal-mail-redelivery.com instead of royalmail.com'],
        ['**Don\'t trust texts that know your name** - personal details are widely available from data breaches', '**Question any text requiring "immediate action"** - real-world deadlines are rarely measured in hours', '**Screenshot suspicious texts before deleting** - they can help your bank or the police if needed', '**Keep your phone\'s OS updated** - security patches close vulnerabilities that smishing links try to exploit'],
      ],
      phone: [
        ['**Hang up and call back on an official number** - never trust a number provided by the caller', '**Never share PINs, passwords, or OTPs** over the phone - no legitimate company asks for these', '**Be wary of unsolicited calls about "problems"** - real companies usually contact you by letter first', '**Don\'t download software** when asked by an unsolicited caller claiming to be tech support'],
        ['**Caller ID can be spoofed** - seeing your bank\'s name does not mean it is really them', '**Take your time** - scammers create artificial urgency to prevent you from thinking clearly', '**Ask callers for a reference number** then call the organisation back on their published number', '**Never move money to a "safe account"** - no bank or authority will ever ask you to do this'],
        ['**Watch for "authority" pressure** - scammers often pretend to be police, HMRC, or government officials', '**Be suspicious of pre-recorded messages** - automated calls asking you to "press 1" are almost always scams', '**Never give remote access to your computer** - no genuine company will ask for this in an unexpected call', '**Scammers can keep the line open** - if you hang up and immediately redial, you may still be connected to them. Wait 5 minutes.'],
        ['**Real companies don\'t demand gift card payments** - if a caller asks for iTunes or Amazon cards, it\'s a scam', '**Be cautious of "confirmation" calls** - scammers may already know some of your details from data breaches', '**Register with the Telephone Preference Service (TPS)** - it reduces nuisance calls and makes remaining ones more suspicious', '**Never confirm your full name or address** to an unexpected caller - they may be harvesting your details'],
      ],
    };
    const pool = tipPools[channel] || tipPools.email;
    const idx = (Date.now() % (pool.length * 7)) % pool.length;
    return pool[idx];
  }
}

// Export singleton instance
export const openAIService = new OpenAIService();
